import { useState, useEffect, useCallback } from 'react';
import { Photo } from '../types';
import { useSignedUrl } from '../hooks/useSignedUrl';
import './ImageViewer.css';

interface ImageViewerProps {
  photo: Photo | null;
  photos: Photo[];
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (photo: Photo) => void;
}

const ImageViewer = ({ photo, photos, isOpen, onClose, onNavigate }: ImageViewerProps) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get signed URL for full image with fallback to original URL
  const { url: signedFullImageUrl, loading: urlLoading } = useSignedUrl(
    photo?.s3Key || null,
    'full',
    { 
      enabled: isOpen && !!photo,
      fallbackUrl: photo?.fullImageUrl 
    }
  );

  // Find current photo index in the photos array
  useEffect(() => {
    if (photo && photos.length > 0) {
      const index = photos.findIndex(p => p.id === photo.id);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  }, [photo, photos]);

  // Reset states when photo changes
  useEffect(() => {
    if (photo) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [photo]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        navigateToPrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        navigateToNext();
        break;
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navigateToPrevious = () => {
    if (photos.length === 0) return;
    
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
    const prevPhoto = photos[prevIndex];
    
    if (prevPhoto && onNavigate) {
      onNavigate(prevPhoto);
    }
  };

  const navigateToNext = () => {
    if (photos.length === 0) return;
    
    const nextIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
    const nextPhoto = photos[nextIndex];
    
    if (nextPhoto && onNavigate) {
      onNavigate(nextPhoto);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleRetryLoad = () => {
    setImageLoading(true);
    setImageError(false);
    // Force image reload by updating src
    const img = document.querySelector('.image-viewer-img') as HTMLImageElement;
    if (img && signedFullImageUrl) {
      img.src = signedFullImageUrl + '?retry=' + Date.now();
    }
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !photo) {
    return null;
  }

  const canNavigate = photos.length > 1;
  const isFirstPhoto = currentIndex === 0;
  const isLastPhoto = currentIndex === photos.length - 1;

  return (
    <div className="image-viewer-overlay" onClick={handleBackdropClick}>
      <div className="image-viewer-container">
        {/* Header with close button and photo info */}
        <div className="image-viewer-header">
          <div className="image-viewer-info">
            <h3 className="image-viewer-filename">{photo.filename}</h3>
            {canNavigate && (
              <span className="image-viewer-counter">
                {currentIndex + 1} of {photos.length}
              </span>
            )}
          </div>
          <button
            className="image-viewer-close"
            onClick={onClose}
            aria-label="Close image viewer"
          >
            ×
          </button>
        </div>

        {/* Main image area */}
        <div className="image-viewer-content">
          {/* Previous button */}
          {canNavigate && (
            <button
              className={`image-viewer-nav image-viewer-nav-prev ${isFirstPhoto ? 'disabled' : ''}`}
              onClick={navigateToPrevious}
              disabled={isFirstPhoto}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}

          {/* Image container */}
          <div className="image-viewer-image-container">
            {(imageLoading || urlLoading) && (
              <div className="image-viewer-loading">
                <div className="loading-spinner"></div>
                <p>Loading image...</p>
              </div>
            )}

            {imageError && (
              <div className="image-viewer-error">
                <div className="error-icon">⚠️</div>
                <h4>Failed to load image</h4>
                <p>The image could not be loaded. Please try again.</p>
                <button
                  className="retry-button"
                  onClick={handleRetryLoad}
                >
                  Retry
                </button>
              </div>
            )}

            {!imageError && signedFullImageUrl && (
              <img
                className="image-viewer-img"
                src={signedFullImageUrl}
                alt={photo.filename}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: (imageLoading || urlLoading) ? 'none' : 'block' }}
              />
            )}
          </div>

          {/* Next button */}
          {canNavigate && (
            <button
              className={`image-viewer-nav image-viewer-nav-next ${isLastPhoto ? 'disabled' : ''}`}
              onClick={navigateToNext}
              disabled={isLastPhoto}
              aria-label="Next image"
            >
              ›
            </button>
          )}
        </div>

        {/* Footer with metadata */}
        <div className="image-viewer-footer">
          <div className="image-viewer-metadata">
            <span>
              {photo.metadata.width} × {photo.metadata.height} pixels
            </span>
            <span>
              {(photo.metadata.fileSize / 1024 / 1024).toFixed(2)} MB
            </span>
            <span>
              {photo.metadata.format.toUpperCase()}
            </span>
            <span>
              {photo.metadata.exifDate 
                ? `Taken: ${new Date(photo.metadata.exifDate).toLocaleDateString()}`
                : `Uploaded: ${new Date(photo.createdAt).toLocaleDateString()}`
              }
            </span>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="image-viewer-shortcuts">
          <small>
            Use ← → arrow keys to navigate • ESC to close
          </small>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;